// src/features/ranking/pages/Ranking.tsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useSelector,
} from 'react-redux';

import {
  useNavigate,
} from 'react-router-dom';

import type {
  RootState,
} from '../../../app/store';

import {
  storeService,
} from '../../store/services/storeService';

import type {
  StoreListItem,
} from '../../store/types/store.types';

import {
  rankingService,
} from '../services/rankingService';

import type {
  RankingItem,
} from '../types/ranking.types';

import {
  formatMonthTitle,
  getCurrentMonth,
  getInitial,
  getPodiumStyle,
  getRankBadgeClassName,
  moveMonth,
} from '../utils/ranking.utils';


export default function Ranking():
  React.JSX.Element {
  const navigate =
    useNavigate();


  const currentUser =
    useSelector(
      (
        state:
          RootState,
      ) =>
        state.auth.user,
    );


  const userStoreId =
    currentUser?.storeId ??
    0;


  const currentMonth =
    useMemo(
      () =>
        getCurrentMonth(),
      [],
    );


  const [
    storeList,
    setStoreList,
  ] =
    useState<
      StoreListItem[]
    >([]);


  const [
    selectedStoreId,
    setSelectedStoreId,
  ] =
    useState<number>(
      0,
    );


  const [
    selectedMonth,
    setSelectedMonth,
  ] =
    useState<string>(
      currentMonth,
    );


  const [
    rankings,
    setRankings,
  ] =
    useState<
      RankingItem[]
    >([]);


  const [
    isLoadingStores,
    setIsLoadingStores,
  ] =
    useState(true);


  const [
    isLoadingRankings,
    setIsLoadingRankings,
  ] =
    useState(false);


  const [
    storeError,
    setStoreError,
  ] =
    useState('');


  const [
    rankingError,
    setRankingError,
  ] =
    useState('');


  /**
   * 선택된 매장
   */
  const selectedStore =
    useMemo(
      () =>
        storeList.find(
          (
            store,
          ) =>
            store.storeId ===
            selectedStoreId,
        ),
      [
        selectedStoreId,
        storeList,
      ],
    );


  /**
   * 랭킹 정렬
   */
  const sortedRankings =
    useMemo(
      () =>
        [
          ...rankings,
        ].sort(
          (
            first,
            second,
          ) =>
            first.rank -
            second.rank,
        ),
      [
        rankings,
      ],
    );


  /**
   * 모바일 TOP 10
   */
  const mobileTopTenRankings =
    useMemo(
      () =>
        sortedRankings.slice(
          0,
          10,
        ),
      [
        sortedRankings,
      ],
    );


  /**
   * 1 ~ 3위
   */
  const podiumRankings =
    useMemo(
      () =>
        [
          1,
          2,
          3,
        ]
          .map(
            (
              rank,
            ) =>
              sortedRankings.find(
                (
                  ranking,
                ) =>
                  ranking.rank ===
                  rank,
              ),
          )
          .filter(
            (
              ranking,
            ): ranking is RankingItem =>
              Boolean(
                ranking,
              ),
          ),
      [
        sortedRankings,
      ],
    );


  const isNextMonthDisabled =
    selectedMonth >=
    currentMonth;


  /**
   * 매장 목록 조회
   */
  const fetchStores =
    useCallback(
      async (): Promise<void> => {
        try {
          setIsLoadingStores(
            true,
          );

          setStoreError(
            '',
          );


          const response =
            await storeService
              .getStores();


          const stores =
            Array.isArray(
              response,
            )
              ? response.filter(
                  (
                    store,
                  ) =>
                    store.storeId !==
                    0,
                )
              : [];


          setStoreList(
            stores,
          );


          if (
            stores.length ===
            0
          ) {
            setSelectedStoreId(
              0,
            );


            setStoreError(
              '조회할 수 있는 매장이 없습니다.',
            );


            return;
          }


          setSelectedStoreId(
            (
              previous,
            ) => {
              /**
               * 기존 선택 매장이 있으면 유지
               */
              const previousExists =
                previous > 0 &&
                stores.some(
                  (
                    store,
                  ) =>
                    store.storeId ===
                    previous,
                );


              if (
                previousExists
              ) {
                return previous;
              }


              /**
               * 로그인 사용자의 소속 매장을
               * 우선 선택
               */
              const userStoreExists =
                userStoreId >
                  0 &&
                stores.some(
                  (
                    store,
                  ) =>
                    store.storeId ===
                    userStoreId,
                );


              if (
                userStoreExists
              ) {
                return userStoreId;
              }


              /**
               * 없으면 첫 번째 매장
               */
              return stores[
                0
              ].storeId;
            },
          );

        } catch (
          error: unknown
        ) {
          console.error(
            '매장 목록 조회 실패:',
            error,
          );


          setStoreList(
            [],
          );


          setSelectedStoreId(
            0,
          );


          setStoreError(
            '매장 목록을 불러오지 못했습니다.',
          );

        } finally {
          setIsLoadingStores(
            false,
          );
        }
      },
      [
        userStoreId,
      ],
    );


  /**
   * 월간 랭킹 조회
   */
  const fetchRankings =
    useCallback(
      async (): Promise<void> => {
        if (
          selectedStoreId <=
            0 ||
          !selectedMonth
        ) {
          setRankings(
            [],
          );

          return;
        }


        try {
          setIsLoadingRankings(
            true,
          );


          setRankingError(
            '',
          );


          const response =
            await rankingService
              .getStoreRanking(
                selectedStoreId,
                selectedMonth,
              );


          setRankings(
            Array.isArray(
              response.rankings,
            )
              ? response.rankings
              : [],
          );

        } catch (
          error: unknown
        ) {
          console.error(
            '랭킹 조회 실패:',
            error,
          );


          setRankings(
            [],
          );


          setRankingError(
            '랭킹 정보를 불러오지 못했습니다.',
          );

        } finally {
          setIsLoadingRankings(
            false,
          );
        }
      },
      [
        selectedMonth,
        selectedStoreId,
      ],
    );


  /**
   * 최초 매장 목록 조회
   */
  useEffect(
    () => {
      void fetchStores();
    },
    [
      fetchStores,
    ],
  );


  /**
   * 매장 또는 조회 월이 변경되면
   * 랭킹 재조회
   */
  useEffect(
    () => {
      if (
        selectedStoreId <=
        0
      ) {
        setRankings(
          [],
        );

        return;
      }


      void fetchRankings();
    },
    [
      fetchRankings,
      selectedStoreId,
    ],
  );


  /**
   * 이전 달
   */
  const handlePreviousMonth =
    (): void => {
      setSelectedMonth(
        (
          previous,
        ) =>
          moveMonth(
            previous,
            -1,
          ),
      );
    };


  /**
   * 다음 달
   */
  const handleNextMonth =
    (): void => {
      if (
        isNextMonthDisabled
      ) {
        return;
      }


      setSelectedMonth(
        (
          previous,
        ) => {
          const nextMonth =
            moveMonth(
              previous,
              1,
            );


          return (
            nextMonth >
            currentMonth
              ? currentMonth
              : nextMonth
          );
        },
      );
    };


  /**
   * 월 직접 선택
   */
  const handleMonthChange = (
    value: string,
  ): void => {
    if (!value) {
      return;
    }


    setSelectedMonth(
      value >
        currentMonth
        ? currentMonth
        : value,
    );
  };


  /**
   * 프로필 이미지
   */
  const renderProfileImage = (
    ranking:
      RankingItem,
    className: string,
  ): React.ReactNode => {
    return (
      <div
        className={`
          flex shrink-0 items-center
          justify-center overflow-hidden
          rounded-full bg-gray-100
          font-bold text-gray-600
          ${className}
        `}
      >

        {ranking.profileImageUrl ? (

          <img
            src={
              ranking.profileImageUrl
            }
            alt={`${ranking.nickname} 프로필`}
            className="h-full w-full object-cover"
          />

        ) : (

          getInitial(
            ranking.nickname,
          )

        )}

      </div>
    );
  };


  return (
    <main className="min-h-screen bg-gray-50 px-3 py-4 sm:px-6 sm:py-8 lg:px-8">

      <div className="mx-auto w-full max-w-4xl">


        {/* =================================================
            상단 제목
            ================================================= */}
        <header className="mb-3 sm:mb-8">

          <div className="flex items-start justify-between gap-3">

            <div className="flex min-w-0 items-start gap-2 sm:gap-3">

              {/* 뒤로가기 */}
              <button
                type="button"
                onClick={() =>
                  navigate(
                    -1,
                  )
                }
                aria-label="이전 페이지로 이동"
                title="뒤로가기"
                className="
                  mt-3.5 flex h-8 w-8 shrink-0
                  items-center justify-center
                  rounded-full border
                  border-gray-200 bg-white
                  text-gray-500 shadow-sm
                  transition
                  hover:border-main
                  hover:text-main
                  focus:outline-none
                  focus:ring-2
                  focus:ring-main/20
                  sm:mt-4.5 sm:h-9 sm:w-9
                "
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 18l-6-6 6-6"
                  />
                </svg>
              </button>


              <div className="min-w-0">

                <p className="text-[10px] font-bold tracking-[0.16em] text-main sm:text-xs sm:tracking-[0.18em]">
                  MONTHLY RANKING
                </p>


                <h1 className="mt-0.5 truncate text-xl font-bold tracking-tight text-gray-900 sm:mt-1 sm:text-3xl">
                  월간 승점 랭킹
                </h1>


                <p className="mt-1 hidden text-sm leading-6 text-gray-500 sm:block">
                  매장과 조회 월을 선택하여 월별
                  승점 순위를 확인할 수 있습니다.
                </p>

              </div>

            </div>


            {/* 새로고침 */}
            <button
              type="button"
              onClick={() => {
                void fetchRankings();
              }}
              disabled={
                isLoadingRankings ||
                selectedStoreId <=
                  0
              }
              aria-label="랭킹 새로고침"
              title="랭킹 새로고침"
              className="
                flex h-9 w-9 shrink-0
                items-center justify-center
                rounded-full border
                border-gray-200 bg-white
                text-gray-500 shadow-sm
                transition hover:border-main
                hover:text-main
                disabled:cursor-not-allowed
                disabled:opacity-50
                sm:h-10 sm:w-auto
                sm:rounded-lg sm:px-3
              "
            >
              <svg
                className={`
                  h-4 w-4
                  ${
                    isLoadingRankings
                      ? 'animate-spin'
                      : ''
                  }
                `}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20 11a8.1 8.1 0 00-15.5-3M4 4v4h4M4 13a8.1 8.1 0 0015.5 3M20 20v-4h-4"
                />
              </svg>


              <span className="hidden sm:ml-1.5 sm:inline">
                새로고침
              </span>
            </button>

          </div>

        </header>


        {/* =================================================
            조회 조건
            ================================================= */}
        <section
          className="
            mb-3 rounded-xl border
            border-gray-100 bg-white
            p-3 shadow-sm
            sm:mb-6 sm:rounded-2xl sm:p-5
          "
        >

          <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">


            {/* 매장 선택 */}
            <div>

              <div className="mb-1 flex items-center justify-between gap-2 sm:mb-1.5">

                <label
                  htmlFor="storeId"
                  className="block text-xs font-semibold text-gray-700 sm:text-sm"
                >
                  매장 선택
                </label>


                {userStoreId >
                  0 &&
                  selectedStoreId ===
                    userStoreId && (

                  <span
                    className="
                      rounded-full bg-main/10
                      px-1.5 py-0.5
                      text-[9px] font-semibold
                      text-main sm:px-2
                      sm:text-[11px]
                    "
                  >
                    내 소속 매장
                  </span>

                )}

              </div>


              <div className="relative">

                <select
                  id="storeId"
                  name="storeId"
                  value={
                    selectedStoreId >
                    0
                      ? String(
                          selectedStoreId,
                        )
                      : ''
                  }
                  disabled={
                    isLoadingStores ||
                    storeList.length ===
                      0
                  }
                  onChange={(
                    event,
                  ) =>
                    setSelectedStoreId(
                      event.target.value
                        ? Number(
                            event.target.value,
                          )
                        : 0,
                    )
                  }
                  className="
                    h-10 w-full appearance-none
                    rounded-lg border border-gray-300
                    bg-white px-3 pr-9
                    text-xs text-gray-900
                    outline-none transition
                    focus:border-main
                    focus:ring-2
                    focus:ring-main/20
                    disabled:cursor-not-allowed
                    disabled:bg-gray-100
                    disabled:text-gray-500
                    sm:h-12 sm:rounded-xl
                    sm:px-4 sm:pr-10
                    sm:text-sm
                  "
                >

                  <option
                    value=""
                    disabled
                  >
                    {isLoadingStores
                      ? '매장 목록 불러오는 중'
                      : '매장을 선택해주세요'}
                  </option>


                  {storeList.map(
                    (
                      store,
                    ) => (

                      <option
                        key={
                          store.storeId
                        }
                        value={
                          store.storeId
                        }
                      >
                        {store.storeName}

                        {store.storeId ===
                        userStoreId
                          ? ' · 내 매장'
                          : ''}
                      </option>

                    ),
                  )}

                </select>


                <svg
                  className="
                    pointer-events-none
                    absolute right-2.5 top-1/2
                    h-4 w-4 -translate-y-1/2
                    text-gray-400
                    sm:right-3 sm:h-5 sm:w-5
                  "
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.09 1.03l-4.25 4.5a.75.75 0 01-1.09 0l-4.25-4.5a.75.75 0 01.02-1.05z"
                  />
                </svg>

              </div>

            </div>


            {/* 월 선택 */}
            <div>

              <label
                htmlFor="rankingMonth"
                className="mb-1 block text-xs font-semibold text-gray-700 sm:mb-1.5 sm:text-sm"
              >
                조회 월
              </label>


              <div className="flex items-stretch gap-1.5 sm:gap-2">

                <button
                  type="button"
                  onClick={
                    handlePreviousMonth
                  }
                  className="
                    flex h-10 w-10 shrink-0
                    items-center justify-center
                    rounded-lg border
                    border-gray-300 bg-white
                    text-gray-500 transition
                    hover:border-main
                    hover:text-main
                    sm:h-12 sm:w-12
                    sm:rounded-xl
                  "
                  aria-label="이전 달"
                >
                  <svg
                    className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 18l-6-6 6-6"
                    />
                  </svg>
                </button>


                <input
                  id="rankingMonth"
                  name="rankingMonth"
                  type="month"
                  value={
                    selectedMonth
                  }
                  max={
                    currentMonth
                  }
                  onChange={(
                    event,
                  ) =>
                    handleMonthChange(
                      event.target.value,
                    )
                  }
                  className="
                    h-10 min-w-0 flex-1
                    rounded-lg border
                    border-gray-300 bg-white
                    px-2.5 text-xs
                    text-gray-900 outline-none
                    transition focus:border-main
                    focus:ring-2
                    focus:ring-main/20
                    sm:h-12 sm:rounded-xl
                    sm:px-4 sm:text-sm
                  "
                />


                <button
                  type="button"
                  onClick={
                    handleNextMonth
                  }
                  disabled={
                    isNextMonthDisabled
                  }
                  className="
                    flex h-10 w-10 shrink-0
                    items-center justify-center
                    rounded-lg border
                    border-gray-300 bg-white
                    text-gray-500 transition
                    hover:border-main
                    hover:text-main
                    disabled:cursor-not-allowed
                    disabled:bg-gray-100
                    disabled:text-gray-300
                    sm:h-12 sm:w-12
                    sm:rounded-xl
                  "
                  aria-label="다음 달"
                >
                  <svg
                    className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 18l6-6-6-6"
                    />
                  </svg>
                </button>

              </div>

            </div>

          </div>


          {storeError && (

            <div className="mt-2 flex items-center justify-between gap-3 rounded-lg bg-red-50 px-3 py-2 sm:mt-4 sm:px-3.5 sm:py-3">

              <p className="text-xs text-red-600 sm:text-sm">
                {storeError}
              </p>


              <button
                type="button"
                onClick={() => {
                  void fetchStores();
                }}
                disabled={
                  isLoadingStores
                }
                className="
                  shrink-0 text-[11px]
                  font-semibold text-red-700
                  underline underline-offset-2
                  disabled:cursor-not-allowed
                  disabled:opacity-50
                  sm:text-xs
                "
              >
                다시 시도
              </button>

            </div>

          )}

        </section>


        {/* =================================================
            랭킹 제목
            ================================================= */}
        <section className="mb-2 flex items-end justify-between gap-3 sm:mb-5">

          <div className="min-w-0">

            <p className="truncate text-xs font-semibold text-main sm:text-sm">
              {selectedStore?.storeName ||
                '매장을 선택해주세요'}
            </p>


            <h2 className="mt-0.5 text-base font-bold text-gray-900 sm:text-2xl">
              {formatMonthTitle(
                selectedMonth,
              )}{' '}
              랭킹
            </h2>

          </div>


          {!isLoadingRankings &&
            !rankingError && (

            <p className="shrink-0 text-[10px] text-gray-400 sm:text-sm">
              총{' '}
              {sortedRankings.length}
              명
            </p>

          )}

        </section>


        {/* =================================================
            Loading
            ================================================= */}
        {isLoadingRankings && (

          <section
            className="
              flex min-h-52 items-center
              justify-center rounded-xl
              border border-gray-100
              bg-white shadow-sm
              sm:min-h-72 sm:rounded-2xl
            "
          >

            <div className="flex flex-col items-center gap-2 sm:gap-3">

              <span
                className="
                  h-7 w-7 animate-spin
                  rounded-full border-4
                  border-gray-200
                  border-t-main
                  sm:h-9 sm:w-9
                "
                aria-hidden="true"
              />


              <p className="text-xs text-gray-500 sm:text-sm">
                랭킹 정보를 불러오고 있습니다.
              </p>

            </div>

          </section>

        )}


        {/* =================================================
            Error
            ================================================= */}
        {!isLoadingRankings &&
          rankingError && (

          <section
            className="
              flex min-h-52 flex-col
              items-center justify-center
              rounded-xl border
              border-red-100 bg-white
              px-4 py-6 text-center
              shadow-sm sm:min-h-64
              sm:rounded-2xl sm:px-5
              sm:py-8
            "
          >

            <div
              className="
                flex h-12 w-12
                items-center justify-center
                rounded-full bg-red-50
                text-red-500
                sm:h-14 sm:w-14
              "
            >
              <svg
                className="h-6 w-6 sm:h-7 sm:w-7"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v4m0 4h.01M10.3 4.4L2.7 17.5A1.7 1.7 0 004.2 20h15.6a1.7 1.7 0 001.5-2.5L13.7 4.4a2 2 0 00-3.4 0z"
                />
              </svg>
            </div>


            <h3 className="mt-3 text-sm font-bold text-gray-900 sm:mt-4 sm:text-base">
              랭킹을 불러오지 못했습니다
            </h3>


            <p className="mt-1.5 text-xs leading-5 text-gray-500 sm:mt-2 sm:text-sm sm:leading-6">
              {rankingError}
            </p>


            <button
              type="button"
              onClick={() => {
                void fetchRankings();
              }}
              className="
                mt-4 flex h-9 items-center
                justify-center rounded-lg
                bg-main px-3 text-xs
                font-semibold text-white
                transition
                hover:bg-main-medium
                sm:mt-5 sm:h-10
                sm:px-4 sm:text-sm
              "
            >
              다시 시도
            </button>

          </section>

        )}


        {/* =================================================
            Empty
            ================================================= */}
        {!isLoadingRankings &&
          !rankingError &&
          sortedRankings.length ===
            0 && (

          <section
            className="
              flex min-h-52 flex-col
              items-center justify-center
              rounded-xl border
              border-gray-100 bg-white
              px-4 py-6 text-center
              shadow-sm sm:min-h-64
              sm:rounded-2xl sm:px-5
              sm:py-8
            "
          >

            <div
              className="
                flex h-12 w-12
                items-center justify-center
                rounded-full bg-gray-100
                text-gray-400
                sm:h-14 sm:w-14
              "
            >
              <svg
                className="h-6 w-6 sm:h-7 sm:w-7"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 21h8M12 17v4M7 4h10v4a5 5 0 01-10 0V4zM7 6H4v1a4 4 0 004 4M17 6h3v1a4 4 0 01-4 4"
                />
              </svg>
            </div>


            <h3 className="mt-3 text-sm font-bold text-gray-900 sm:mt-4 sm:text-base">
              아직 등록된 랭킹이 없습니다
            </h3>


            <p className="mt-1.5 text-xs leading-5 text-gray-500 sm:mt-2 sm:text-sm sm:leading-6">
              선택한 매장과 월의 승점 기록이 없습니다.
            </p>

          </section>

        )}


        {/* =================================================
            Ranking
            ================================================= */}
        {!isLoadingRankings &&
          !rankingError &&
          sortedRankings.length >
            0 && (

          <>

            {/* 데스크톱 포디움 */}
            <section
              className="
                mb-6 hidden
                grid-cols-3 items-end
                gap-4 pt-4 sm:grid
              "
            >

              {podiumRankings.map(
                (
                  ranking,
                ) => {
                  const style =
                    getPodiumStyle(
                      ranking.rank,
                    );


                  return (
                    <article
                      key={
                        ranking.userId
                      }
                      className={`
                        relative flex flex-col
                        items-center rounded-2xl
                        border px-4 py-5
                        text-center transition
                        hover:-translate-y-1
                        ${style.wrapper}
                        ${style.order}
                      `}
                    >

                      <span
                        className={`
                          absolute -top-2.5
                          flex h-7 min-w-7
                          items-center justify-center
                          rounded-full px-2
                          text-xs font-bold
                          shadow-sm
                          ${style.badge}
                        `}
                      >
                        {ranking.rank}
                      </span>


                      {renderProfileImage(
                        ranking,
                        style.avatar,
                      )}


                      <h3 className="mt-3 max-w-full truncate font-bold text-gray-900">
                        {ranking.nickname}
                      </h3>


                      <div className="mt-1 flex items-baseline gap-1">

                        <strong className="text-2xl font-black text-gray-900">
                          {ranking.score.toLocaleString()}
                        </strong>


                        <span className="text-xs font-medium text-gray-400">
                          점
                        </span>

                      </div>


                      {ranking.rank ===
                        1 && (

                        <span
                          className="
                            mt-3 rounded-full
                            bg-amber-100 px-3 py-1
                            text-[11px] font-bold
                            text-amber-700
                          "
                        >
                          MONTHLY WINNER
                        </span>

                      )}

                    </article>
                  );
                },
              )}

            </section>


            {/* 모바일 TOP 10 */}
            <section
              className="
                overflow-hidden rounded-xl
                border border-gray-100
                bg-white shadow-sm sm:hidden
              "
            >

              <div
                className="
                  flex h-8 items-center
                  justify-between
                  border-b border-gray-100
                  px-3
                "
              >

                <h3 className="text-xs font-bold text-gray-900">
                  TOP 10
                </h3>


                <span className="text-[10px] text-gray-400">
                  승점 순
                </span>

              </div>


              <ol className="divide-y divide-gray-100">

                {mobileTopTenRankings.map(
                  (
                    ranking,
                  ) => (

                    <li
                      key={
                        ranking.userId
                      }
                      className="
                        flex h-10 items-center
                        gap-2 px-3
                      "
                    >

                      <span
                        className={`
                          flex h-6 w-6 shrink-0
                          items-center justify-center
                          rounded-full text-[10px]
                          font-black
                          ${getRankBadgeClassName(
                            ranking.rank,
                          )}
                        `}
                      >
                        {ranking.rank}
                      </span>


                      <div className="min-w-0 flex-1">

                        <p className="truncate text-xs font-semibold text-gray-900">
                          {ranking.nickname}
                        </p>

                      </div>


                      <div className="shrink-0 text-right">

                        <strong className="text-xs font-black text-gray-900">
                          {ranking.score.toLocaleString()}
                        </strong>


                        <span className="ml-0.5 text-[9px] text-gray-400">
                          점
                        </span>

                      </div>

                    </li>

                  ),
                )}

              </ol>


              {sortedRankings.length >
                10 && (

                <div className="flex h-8 items-center justify-center border-t border-gray-100 text-[10px] text-gray-400">
                  상위 10명까지 표시됩니다.
                </div>

              )}

            </section>


            {/* 태블릿 / 데스크톱 전체 랭킹 */}
            <section
              className="
                hidden overflow-hidden
                rounded-2xl border
                border-gray-100 bg-white
                shadow-sm sm:block
              "
            >

              <div
                className="
                  flex items-center justify-between
                  border-b border-gray-100
                  px-5 py-4
                "
              >

                <div>

                  <h3 className="font-bold text-gray-900">
                    전체 순위
                  </h3>


                  <p className="mt-0.5 text-xs text-gray-400">
                    승점이 높은 순서로 표시됩니다.
                  </p>

                </div>


                <span
                  className="
                    rounded-full bg-main/10
                    px-2.5 py-1 text-xs
                    font-bold text-main
                  "
                >
                  TOP{' '}
                  {sortedRankings.length}
                </span>

              </div>


              <ol className="divide-y divide-gray-100">

                {sortedRankings.map(
                  (
                    ranking,
                  ) => (

                    <li
                      key={
                        ranking.userId
                      }
                      className="
                        flex items-center gap-4
                        px-5 py-4 transition
                        hover:bg-gray-50
                      "
                    >

                      <span
                        className={`
                          flex h-8 w-8 shrink-0
                          items-center justify-center
                          rounded-full text-xs
                          font-black
                          ${getRankBadgeClassName(
                            ranking.rank,
                          )}
                        `}
                      >
                        {ranking.rank}
                      </span>


                      {renderProfileImage(
                        ranking,
                        'h-11 w-11 text-sm',
                      )}


                      <div className="min-w-0 flex-1">

                        <p className="truncate text-base font-bold text-gray-900">
                          {ranking.nickname}
                        </p>


                        <p className="mt-0.5 text-xs text-gray-400">
                          USER #
                          {ranking.userId}
                        </p>

                      </div>


                      <div className="shrink-0 text-right">

                        <strong className="text-lg font-black text-gray-900">
                          {ranking.score.toLocaleString()}
                        </strong>


                        <span className="ml-1 text-xs text-gray-400">
                          점
                        </span>

                      </div>

                    </li>

                  ),
                )}

              </ol>

            </section>

          </>

        )}

      </div>

    </main>
  );
}